import { Download, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface MeasurementSummaryProps {
  measurementData: any;
  measurementSource?: string;
  fileUrl?: string;
  pdfUrl?: string;
}

export function MeasurementSummarySection({ 
  measurementData, 
  measurementSource = 'manual',
  fileUrl,
  pdfUrl
}: MeasurementSummaryProps) {
  if (!measurementData) return null;

  const measurements = measurementData.measurements || measurementData;
  const { area_sqft, pitch, ridge_lf, valley_lf, eave_lf, rake_lf } = measurements;

  if (!area_sqft) return null;

  return (
    <section className="my-8 page-break-inside-avoid">
      <div className="border rounded-lg p-6 bg-muted/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-foreground">Property Measurement Report</h3>
          <Badge variant="outline">
            {measurementSource === 'xml' ? 'GAF QuickMeasure' : measurementSource === 'dxf' ? 'DXF Plan' : 'Manual Entry'}
          </Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <MeasurementItem label="Total Area" value={`${Math.round(area_sqft)} sq ft`} />
          {pitch && <MeasurementItem label="Roof Pitch" value={pitch} />}
          {ridge_lf && <MeasurementItem label="Ridge Length" value={`${Math.round(ridge_lf)} LF`} />}
          {valley_lf && <MeasurementItem label="Valley Length" value={`${Math.round(valley_lf)} LF`} />}
          {eave_lf && <MeasurementItem label="Eave Length" value={`${Math.round(eave_lf)} LF`} />}
          {rake_lf && <MeasurementItem label="Rake Length" value={`${Math.round(rake_lf)} LF`} />}
        </div>

        <p className="text-sm text-muted-foreground">
          {measurementSource === 'xml' 
            ? 'Measurements captured using professional aerial roof measurement technology'
            : measurementSource === 'dxf'
            ? 'Measurements extracted from architectural plans'
            : 'Measurements recorded during property inspection'}
        </p>

        <div className="flex gap-2 mt-4 no-print">
          {fileUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={fileUrl} download>
                <Download className="mr-2 h-4 w-4" />
                Download XML/DXF
              </a>
            </Button>
          )}
          {pdfUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={pdfUrl} download target="_blank" rel="noopener noreferrer">
                <Download className="mr-2 h-4 w-4" />
                Download PDF Report
              </a>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function MeasurementItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}
